import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useContrats } from "@/pages/proprietaire/contrats/ContratsProvider";
import LocataireForm from "./components/LocataireForm";
import { useLocataires } from "./LocatairesProvider";
import type { ExportColumn } from "@/utils/export";
import { formatDate } from "@/utils/format";
import {
  TYPE_PIECE_LABELS,
  TYPE_PIECE_OPTIONS,
  locataireInitiales,
  locataireNomComplet,
  type Locataire,
} from "./types";

type SortKey = "recent" | "nom";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "nom", label: "Nom (A → Z)" },
];

const pieceFilterOptions = [
  { value: "", label: "Toutes les pièces" },
  ...TYPE_PIECE_OPTIONS,
];

const situationFilterOptions = [
  { value: "", label: "Toutes les situations" },
  { value: "actif", label: "Avec contrat actif" },
  { value: "sans", label: "Sans contrat actif" },
];

export default function LocatairesPage() {
  const { locataires, removeLocataire } = useLocataires();
  const { contrats, contratsByLocataire } = useContrats();

  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pieceFilter, setPieceFilter] = useState("");
  const [situationFilter, setSituationFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [editing, setEditing] = useState<Locataire | null>(null);
  const [toDelete, setToDelete] = useState<Locataire | null>(null);

  const hasLocataires = locataires.length > 0;

  const activeLocataireIds = useMemo(
    () => new Set(contrats.filter((item) => item.statut === "actif").map((item) => item.locataireId)),
    [contrats]
  );

  const occupiedBiens = useMemo(
    () => new Set(contrats.filter((item) => item.statut === "actif").map((item) => item.bienId)).size,
    [contrats]
  );

  const stats = useMemo(
    () => ({
      total: locataires.length,
      avecContrat: locataires.filter((item) => activeLocataireIds.has(item.id)).length,
      sansContrat: locataires.filter((item) => !activeLocataireIds.has(item.id)).length,
    }),
    [locataires, activeLocataireIds]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = locataires.filter((locataire) => {
      const matchesPiece = !pieceFilter || locataire.typePiece === pieceFilter;
      const hasActive = activeLocataireIds.has(locataire.id);
      const matchesSituation =
        !situationFilter || (situationFilter === "actif" ? hasActive : !hasActive);
      const matchesSearch =
        !term ||
        [
          locataire.prenom,
          locataire.nom,
          locataire.telephone,
          locataire.whatsapp,
          locataire.email,
          locataire.profession,
          locataire.numeroPiece,
          locataire.adresse,
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesPiece && matchesSituation && matchesSearch;
    });

    return result.sort((a, b) => {
      if (sort === "nom") return locataireNomComplet(a).localeCompare(locataireNomComplet(b), "fr");
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [locataires, search, pieceFilter, situationFilter, sort, activeLocataireIds]);

  const isFiltering = Boolean(search.trim()) || Boolean(pieceFilter) || Boolean(situationFilter);

  const exportColumns: ExportColumn<Locataire>[] = [
    { header: "Nom complet", value: (locataire) => locataireNomComplet(locataire) },
    { header: "Téléphone", value: (locataire) => locataire.telephone },
    { header: "WhatsApp", value: (locataire) => locataire.whatsapp },
    { header: "Email", value: (locataire) => locataire.email },
    { header: "Profession", value: (locataire) => locataire.profession },
    { header: "Adresse", value: (locataire) => locataire.adresse },
    { header: "Pièce d'identité", value: (locataire) => (locataire.typePiece ? TYPE_PIECE_LABELS[locataire.typePiece] : "") },
    { header: "N° pièce", value: (locataire) => locataire.numeroPiece },
    { header: "Créé le", value: (locataire) => formatDate(locataire.createdAt) },
  ];

  const columns: DataTableColumn<Locataire>[] = [
    {
      key: "identite",
      header: "Locataire",
      className: "min-w-[200px]",
      render: (locataire) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 font-heading text-xs font-semibold text-primary-700">
            {locataireInitiales(locataire)}
          </span>
          <span className="truncate font-label font-medium text-foreground-950">{locataireNomComplet(locataire)}</span>
        </div>
      ),
    },
    {
      key: "profession",
      header: "Profession",
      className: "min-w-[150px]",
      render: (locataire) => <span className="text-foreground-700">{locataire.profession || "Non renseignée"}</span>,
    },
    {
      key: "telephone",
      header: "Téléphone",
      className: "min-w-[140px]",
      render: (locataire) => <span className="text-foreground-700">{locataire.telephone || "Non renseigné"}</span>,
    },
    {
      key: "whatsapp",
      header: "WhatsApp",
      className: "min-w-[140px]",
      render: (locataire) => (
        <span className="text-foreground-700">{locataire.whatsapp || "Non renseigné"}</span>
      ),
    },
    {
      key: "email",
      header: "Email",
      className: "min-w-[200px]",
      render: (locataire) => <span className="text-foreground-700">{locataire.email || "Non renseigné"}</span>,
    },
    {
      key: "piece",
      header: "Pièce",
      render: (locataire) =>
        locataire.typePiece ? (
          <Badge tone="neutral" icon="ri-id-card-line">
            {TYPE_PIECE_LABELS[locataire.typePiece]}
          </Badge>
        ) : (
          <Badge tone="neutral">Sans pièce</Badge>
        ),
    },
    {
      key: "contrats",
      header: "Contrats",
      align: "center",
      render: (locataire) => (
        <span className="font-label font-medium text-foreground-950">{contratsByLocataire(locataire.id).length}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[200px]",
      render: (locataire) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/proprietaire/locataires/${locataire.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir la fiche
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions
            onEdit={() => setEditing(locataire)}
            onDelete={() => setToDelete(locataire)}
          />
        </div>
      ),
    },
  ];

  const handleAdd = () => setFormOpen(true);

  const resetFilters = () => {
    setSearch("");
    setPieceFilter("");
    setSituationFilter("");
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Locataires</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Gérez les fiches de vos locataires : coordonnées, profession, pièce d'identité et contact d'urgence. Chaque
            locataire est ensuite relié à ses contrats et aux biens qu'il occupe.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="locataires" title="Locataires" />
          <button
            type="button"
            onClick={handleAdd}
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-add-line text-base" aria-hidden="true" />
            Ajouter un locataire
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-user-3-line" label="Locataires" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-user-follow-line" label="Avec contrat actif" value={String(stats.avecContrat)} tone="secondary" />
        <StatCard icon="ri-user-unfollow-line" label="Sans contrat actif" value={String(stats.sansContrat)} tone="accent" />
        <StatCard icon="ri-home-smile-2-line" label="Biens occupés" value={String(occupiedBiens)} tone="neutral" />
      </div>

      {hasLocataires && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3 lg:max-w-3xl">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="loc-search" className="text-xs font-label font-medium text-foreground-700">
                  Recherche
                </label>
                <Input
                  id="loc-search"
                  icon="ri-search-line"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Nom, téléphone, email..."
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="loc-piece-filter" className="text-xs font-label font-medium text-foreground-700">
                  Pièce d'identité
                </label>
                <Select
                  id="loc-piece-filter"
                  value={pieceFilter}
                  onChange={(event) => setPieceFilter(event.target.value)}
                  options={pieceFilterOptions}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="loc-situation-filter" className="text-xs font-label font-medium text-foreground-700">
                  Situation
                </label>
                <Select
                  id="loc-situation-filter"
                  value={situationFilter}
                  onChange={(event) => setSituationFilter(event.target.value)}
                  options={situationFilterOptions}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5 sm:w-52">
              <label htmlFor="loc-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id="loc-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} locataire{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}
              {hasLocataires ? ` sur ${locataires.length}` : ""}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {!hasLocataires && (
        <EmptyState
          icon="ri-user-3-line"
          title="Aucun locataire pour le moment"
          description="Ajoutez votre premier locataire avec ses coordonnées et sa pièce d'identité. Vous pourrez ensuite lui rattacher un contrat depuis le module Contrats."
          action={
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un locataire
            </button>
          }
        />
      )}

      {hasLocataires && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun locataire ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(locataire) => locataire.id}
          minWidthClass="min-w-[1140px]"
          caption="Locataires"
        />
      )}

      {hasLocataires && filtered.length > 0 && (
        <p className="text-center text-xs text-foreground-400">
          Pièces acceptées : {Object.values(TYPE_PIECE_LABELS).join(" · ")}.
        </p>
      )}

      <LocataireForm
        open={formOpen || Boolean(editing)}
        initial={editing ?? undefined}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) removeLocataire(toDelete.id);
          setToDelete(null);
        }}
        title="Supprimer le locataire"
        description={
          toDelete ? `Voulez-vous vraiment supprimer « ${locataireNomComplet(toDelete)} » ? Cette action est définitive.` : ""
        }
        blockedMessage={
          toDelete && contrats.some((contrat) => contrat.locataireId === toDelete.id)
            ? "Ce locataire est relié à au moins un contrat. Clôturez ou supprimez d'abord ses contrats avant de le supprimer."
            : undefined
        }
      />
    </div>
  );
}